import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  readDemoToken,
  writeDemoToken,
  readDemoLeadRef,
  writeDemoLeadRef,
  leadRefFromSearch,
} from "../constants/demoMode";
import { postDemoTrack } from "../utils/demoTrack";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const DemoModeContext = createContext(null);

const emptyCredentials = {
  demoClientEmail: null,
  demoClientPassword: null,
  demoAdminPassword: null,
  demoAdminDisplayEmail: null,
};

function applyCredentialsPayload(setStatus, creds) {
  if (!creds) {
    setStatus((prev) => ({ ...prev, ...emptyCredentials }));
    return;
  }
  setStatus((prev) => ({
    ...prev,
    demoClientEmail: creds.client_email || null,
    demoClientPassword: creds.client_password || null,
    demoAdminPassword: creds.admin_password || null,
    demoAdminDisplayEmail: creds.admin_display_email || null,
  }));
}

export function DemoModeProvider({ children }) {
  const [status, setStatus] = useState({
    enabled: false,
    gate: false,
    clientEnabled: false,
    adminEnabled: false,
    loading: true,
    ...emptyCredentials,
  });
  const [token, setToken] = useState(() => readDemoToken());
  const location = useLocation();

  const fetchCredentialsWithVitrineToken = useCallback(async (accessToken) => {
    if (!accessToken) {
      setStatus((prev) => ({ ...prev, ...emptyCredentials }));
      return;
    }
    try {
      const res = await fetch(`${API}/api/demo/credentials`, {
        headers: { "X-Demo-Session": `Bearer ${accessToken}` },
      });
      if (res.status === 401) {
        setStatus((prev) => ({ ...prev, ...emptyCredentials }));
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      applyCredentialsPayload(setStatus, data);
    } catch {
      setStatus((prev) => ({ ...prev, ...emptyCredentials }));
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/demo/status`);
      if (!res.ok) {
        setStatus({
          enabled: false,
          gate: false,
          clientEnabled: false,
          adminEnabled: false,
          loading: false,
          ...emptyCredentials,
        });
        return;
      }
      const data = await res.json();
      const en = !!data.enabled;
      setStatus((prev) => ({
        enabled: en,
        gate: !!data.gate,
        clientEnabled: !!data.client_enabled,
        adminEnabled: !!data.admin_enabled,
        loading: false,
        ...(en
          ? {
              demoClientEmail: prev.demoClientEmail,
              demoClientPassword: prev.demoClientPassword,
              demoAdminPassword: prev.demoAdminPassword,
              demoAdminDisplayEmail: prev.demoAdminDisplayEmail,
            }
          : emptyCredentials),
      }));
    } catch {
      setStatus({
        enabled: false,
        gate: false,
        clientEnabled: false,
        adminEnabled: false,
        loading: false,
        ...emptyCredentials,
      });
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  /** Prospect : ?lead= ou ?lead_ref= sur toute entrée avec query (ex. /demo?lead=acme). */
  useEffect(() => {
    const q = leadRefFromSearch(location.search);
    if (q) writeDemoLeadRef(q);
  }, [location.search]);

  const sessionActivityTracked = useRef(false);
  useEffect(() => {
    if (!status.enabled || !status.gate || !token) return;
    if (sessionActivityTracked.current) return;
    sessionActivityTracked.current = true;
    postDemoTrack("showcase_session_active", { path: location.pathname });
  }, [status.enabled, status.gate, token, location.pathname]);

  useEffect(() => {
    if (status.loading) return;
    const t = readDemoToken();
    if (t && status.enabled && status.gate) {
      fetchCredentialsWithVitrineToken(t);
    } else if (!t) {
      setStatus((prev) => ({ ...prev, ...emptyCredentials }));
    }
  }, [
    status.loading,
    status.enabled,
    status.gate,
    fetchCredentialsWithVitrineToken,
  ]);

  const verifyStoredToken = useCallback(async () => {
    const t = readDemoToken();
    if (!t) return;
    try {
      const res = await fetch(`${API}/api/demo/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: t }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.valid) {
        writeDemoToken(null);
        setToken(null);
      }
    } catch {
      writeDemoToken(null);
      setToken(null);
    }
  }, []);

  useEffect(() => {
    verifyStoredToken();
  }, [verifyStoredToken]);

  const login = useCallback(
    async (password) => {
      const lr = readDemoLeadRef();
      const res = await fetch(`${API}/api/demo/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, lead_ref: lr || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Accès refusé");
      }
      const data = await res.json();
      writeDemoToken(data.access_token);
      setToken(data.access_token);
      if (data.credentials) {
        applyCredentialsPayload(setStatus, data.credentials);
      } else {
        await fetchCredentialsWithVitrineToken(data.access_token);
      }
    },
    [fetchCredentialsWithVitrineToken],
  );

  const logout = useCallback(() => {
    writeDemoToken(null);
    setToken(null);
    setStatus((prev) => ({ ...prev, ...emptyCredentials }));
  }, []);

  const isDemoSession = !!(status.enabled && status.gate && token);

  useEffect(() => {
    if (!isDemoSession) {
      document.documentElement.removeAttribute("data-epp-demo");
      return;
    }
    document.documentElement.setAttribute("data-epp-demo", "1");
    return () => document.documentElement.removeAttribute("data-epp-demo");
  }, [isDemoSession]);

  useEffect(() => {
    if (!isDemoSession) return;
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    return () => {
      /* ne pas rétablir en quittant la page : déploiement démo = tout noindex */
    };
  }, [isDemoSession]);

  const value = useMemo(
    () => ({
      statusLoading: status.loading,
      demoEnabled: status.enabled,
      demoGate: status.gate,
      demoClientEnabled: status.clientEnabled,
      demoAdminEnabled: status.adminEnabled,
      demoClientEmail: status.demoClientEmail,
      demoClientPassword: status.demoClientPassword,
      demoAdminPassword: status.demoAdminPassword,
      demoAdminDisplayEmail: status.demoAdminDisplayEmail,
      isDemoSession,
      token,
      login,
      logout,
      refreshStatus,
      pathname: location.pathname,
    }),
    [
      status.loading,
      status.enabled,
      status.gate,
      status.clientEnabled,
      status.adminEnabled,
      status.demoClientEmail,
      status.demoClientPassword,
      status.demoAdminPassword,
      status.demoAdminDisplayEmail,
      isDemoSession,
      token,
      login,
      logout,
      refreshStatus,
      location.pathname,
    ],
  );

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export function useDemoMode() {
  const ctx = useContext(DemoModeContext);
  if (!ctx) {
    throw new Error("useDemoMode doit être utilisé sous DemoModeProvider");
  }
  return ctx;
}

/** Pour composants pouvant monter hors provider (tests). */
export function useDemoModeOptional() {
  return useContext(DemoModeContext);
}
